import React, { useState } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Alert,
  CircularProgress,
  Paper,
  InputAdornment
} from '@mui/material';
import { trackEvent } from '../../../analytics';
import { 
  createNegotiationCopy, 
  formatCurrency, 
  formatPercentage 
} from '../domain/copyHelpers';

export const TargetIncreasePanel = ({ 
  currentSalary, 
  onCalculate, 
  results, 
  loading = false, 
  error 
}) => {
  const [targetAmount, setTargetAmount] = useState('');
  const [validationError, setValidationError] = useState('');

  const validateTargetAmount = (value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue <= 0) {
      return 'Target must be greater than zero';
    }
    return '';
  };

  const handleTargetChange = (event) => {
    const value = event.target.value;
    setTargetAmount(value);
    
    if (value.trim() === '') {
      setValidationError('');
      return;
    }
    
    const error = validateTargetAmount(value);
    setValidationError(error);
  };

  const handleCalculate = () => {
    const numValue = parseFloat(targetAmount);
    
    if (isNaN(numValue)) {
      setValidationError('Please enter a valid amount');
      return;
    }

    const validationError = validateTargetAmount(numValue);
    if (validationError) {
      setValidationError(validationError);
      return;
    }

    setValidationError('');
    
    // Track analytics
    trackEvent('Pay Rise Simulator', 'Calculate Target Net', `£${numValue}/month`);
    
    onCalculate({
      currentAnnualGross: currentSalary,
      targetNetMonthlyIncrease: numValue
    });
  };

  const isFormValid = targetAmount.trim() !== '' && 
                     !validationError && 
                     !validateTargetAmount(targetAmount);

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Target Increase Calculator
      </Typography>
      
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        How much extra would you like to take home each month? We'll calculate the gross raise you need to ask for.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        <TextField
          label="Target Monthly Increase"
          value={targetAmount}
          onChange={handleTargetChange}
          type="number"
          inputProps={{ 
            min: 0.01, 
            step: 0.01,
            'aria-label': 'Target monthly increase'
          }}
          placeholder="Enter amount"
          disabled={loading}
          error={!!validationError}
          helperText={validationError}
          InputProps={{
            startAdornment: <InputAdornment position="start">£</InputAdornment>
          }}
          fullWidth
        />

        <Button
          variant="contained"
          onClick={handleCalculate}
          disabled={!isFormValid || loading}
          sx={{ alignSelf: 'flex-start' }}
        >
          {loading ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              Calculating...
            </>
          ) : (
            'Calculate Required Raise'
          )}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} role="alert">
          {error}
        </Alert>
      )}

      {results && (
        <Box 
          role="region" 
          aria-live="polite" 
          aria-label="Calculation results"
          sx={{ 
            mt: 3, 
            p: 2, 
            border: 1, 
            borderColor: 'divider', 
            borderRadius: 1,
            bgcolor: 'grey.50'
          }}
        >
          <Typography variant="h6" gutterBottom>
            Negotiation Guidance
          </Typography>

          {/* Main copy line */}
          <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
            {createNegotiationCopy(results.requiredGrossAnnualIncrease, parseFloat(targetAmount))}
          </Typography>

          {/* Detailed breakdown */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mt: 2 }}>
            <Box>
              <Typography variant="subtitle2" color="textSecondary">
                Required Raise Percentage
              </Typography>
              <Typography variant="h5" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                {formatPercentage(results.requiredRaisePctGross)}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" color="textSecondary">
                Required Gross Increase
              </Typography>
              <Typography variant="h5" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                {formatCurrency(results.requiredGrossAnnualIncrease)}
              </Typography>
            </Box>

            <Box sx={{ gridColumn: 'span 2' }}>
              <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                Summary
              </Typography>
              <Typography variant="body2" color="textSecondary">
                To take home an extra {formatCurrency(parseFloat(targetAmount))} per month, 
                you need to negotiate a {formatPercentage(results.requiredRaisePctGross)} raise 
                worth {formatCurrency(results.requiredGrossAnnualIncrease)} annually.
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Paper>
  );
};